import { Box, Chip, IconButton, Typography } from "@mui/material"
import { AddOutlined, DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"

import { Button } from "@/components/common/Button"
import DisplayAS400Id from "@/components/common/DisplayAS400Id/DisplayAS400Id"
import { AerosSelect } from "@/components/UiKit/AerosSelect"
import { PRODUCT_STATUS_CHIP_COLOR_MAP, PRODUCT_STATUS_LABEL_MAP } from "@/constants/product"
import {
  type PriceChangeQuoteRequestProduct,
  PriceChangeType,
  type Product,
  type UnitOfMeasurement,
} from "@/graphql/codegen/graphql"
import PricingProductWeight from "@/screens/Pricing/components/tables/PricingProductWeight"
import { PriceChangeTableVariant, PricingNumericTypeValues } from "@/screens/Pricing/constants"
import { createUomsList } from "@/utils/conversion"
import { formatNumber } from "@/utils/number"

interface PricingProductHeaderProps {
  product: Product
  quoteProduct?: PriceChangeQuoteRequestProduct
  uoms: UnitOfMeasurement[]
  selectedUom: UnitOfMeasurement | undefined
  onUomChange: (id: string) => void
  onAddSpecialPriceClick: () => void
  onAddCostPackageClick: () => void
  onRemoveSubmissionProduct: () => void
  readonly?: boolean
  variant?: PriceChangeTableVariant
  priceChangeType: PriceChangeType
  canRemove?: boolean
}

export const PricingProductHeader: FC<PricingProductHeaderProps> = ({
  product,
  quoteProduct,
  uoms,
  selectedUom,
  onUomChange,
  onAddSpecialPriceClick,
  onAddCostPackageClick,
  onRemoveSubmissionProduct,
  variant,
  readonly,
  priceChangeType,
  canRemove,
}) => {
  const handleUomChange = (value: string | null) => {
    if (!value) return
    onUomChange(value)
  }

  const status = product.computedProductStatus
  const isSpecialPriceDisplaying =
    variant === PriceChangeTableVariant.SubmitPriceChange &&
    !quoteProduct &&
    priceChangeType === PriceChangeType.SellingPrice

  const uomsDropdownList = createUomsList(uoms, product).map(({ id, name }) => ({ value: id ?? "", label: name ?? "" }))

  return (
    <Box className='flex flex-col gap-2 border border-solid border-gray-400 bg-gray-100 p-6'>
      <Box className='flex items-center gap-10'>
        <Box className='flex flex-col'>
          <Box className='flex items-center gap-4'>
            <Typography variant='h5' className='whitespace-nowrap text-gray-700'>
              {product.companyProductDescription}
            </Typography>
            {status && (
              <Chip
                label={PRODUCT_STATUS_LABEL_MAP[status]}
                variant='outlined'
                color={PRODUCT_STATUS_CHIP_COLOR_MAP[status]}
                size='small'
                className='rounded-full'
              />
            )}
          </Box>

          <PricingProductWeight product={product} selectedUom={selectedUom} uoms={uoms} />
        </Box>

        <Box className='flex items-center gap-2'>
          <Box>Product ID {product.productId}</Box>
          <Box>
            <DisplayAS400Id id={product.externalSystemId} />
          </Box>
        </Box>

        {product.company?.name && (
          <Typography variant='body2' className='text-gray-700'>
            Supplier: {product.company.name}
          </Typography>
        )}

        <Box className='flex items-center gap-8'>
          <Box className='flex items-center gap-2'>
            <Typography variant='body2' className='text-gray-700'>
              UoM:
            </Typography>

            <AerosSelect
              options={uomsDropdownList}
              value={selectedUom?.id ?? ""}
              disabled={readonly}
              onChange={handleUomChange}
              className='w-20'
              size='small'
            />
          </Box>

          {isSpecialPriceDisplaying && (
            <Button
              appearance='outlined'
              className='bg-white'
              disabled={readonly}
              onClick={onAddSpecialPriceClick}
              startIcon={<AddOutlined />}
            >
              Add Special Price Package
            </Button>
          )}

          {priceChangeType === PriceChangeType.PurchasingCost && (
            <Button
              appearance='outlined'
              className='bg-white'
              disabled={readonly}
              onClick={onAddCostPackageClick}
              startIcon={<AddOutlined />}
            >
              Add Cost Package
            </Button>
          )}
        </Box>

        {quoteProduct && (
          <>
            <Typography variant='body2' className='text-gray-700'>
              Estimated Annual Volume:{" "}
              <span className='text-gray-800'>
                {typeof quoteProduct.estimatedAnnualVolume === "number"
                  ? formatNumber(quoteProduct.estimatedAnnualVolume, 0, 0)
                  : "—"}
              </span>
            </Typography>
            <Typography variant='body2' className='text-gray-700'>
              Price Point:{" "}
              <span className='text-gray-800'>
                {typeof quoteProduct.pricePoint === "number"
                  ? `${PricingNumericTypeValues.Dollar}${formatNumber(quoteProduct.pricePoint, 2, 2)}`
                  : "—"}
              </span>
            </Typography>
          </>
        )}

        {canRemove && (
          <Box className='ml-auto'>
            <IconButton aria-label='delete parts' onClick={onRemoveSubmissionProduct}>
              <DeleteOutlined />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  )
}
